import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Loader2, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { helpService, SearchResult, stripMarkdown } from '@/services/helpService';
import { cn } from '@/lib/utils';

interface HelpSearchBarProps {
  className?: string;
  size?: 'default' | 'large';
  autoFocus?: boolean;
  initialQuery?: string;
}

export function HelpSearchBar({
  className,
  size = 'default',
  autoFocus = false,
  initialQuery = '',
}: HelpSearchBarProps) {
  const navigate = useNavigate();
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(e.target as Node)
      ) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Debounced search
  const handleSearch = useCallback(async (searchQuery: string) => {
    if (!searchQuery || searchQuery.length < 2) {
      setResults([]);
      setShowDropdown(false);
      return;
    }

    setIsLoading(true);
    try {
      const data = await helpService.search(searchQuery, 5);
      setResults(data.results);
      setShowDropdown(data.results.length > 0);
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Handle input change with debounce
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setQuery(value);

    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      handleSearch(value);
    }, 400);
  };

  // Handle form submit
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      setShowDropdown(false);
      navigate(`/help/search?q=${encodeURIComponent(query.trim())}`);
    }
  };

  // Clear search
  const handleClear = () => {
    setQuery('');
    setResults([]);
    setShowDropdown(false);
    inputRef.current?.focus();
  };

  const isLarge = size === 'large';

  return (
    <div className={cn('relative w-full', className)}>
      <form onSubmit={handleSubmit}>
        <div className="relative">
          <Search
            className={cn(
              'absolute left-4 top-1/2 -translate-y-1/2 text-gray-400',
              isLarge ? 'h-6 w-6' : 'h-5 w-5'
            )}
          />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={handleInputChange}
            onFocus={() => results.length > 0 && setShowDropdown(true)}
            placeholder="Search for answers..."
            autoFocus={autoFocus}
            className={cn(
              'w-full bg-white border border-gray-200 rounded-2xl',
              'pl-12 pr-12 focus:outline-none focus:ring-2 focus:ring-violet-500/30 focus:border-violet-500',
              'placeholder:text-gray-400 text-gray-900 transition-all',
              isLarge
                ? 'py-4 text-lg rounded-2xl'
                : 'py-3 text-base rounded-xl'
            )}
          />
          {isLoading ? (
            <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400 animate-spin" />
          ) : query ? (
            <button
              type="button"
              onClick={handleClear}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            >
              <X className="h-5 w-5" />
            </button>
          ) : null}
        </div>
      </form>

      {/* Instant search results dropdown */}
      {showDropdown && results.length > 0 && (
        <div
          ref={dropdownRef}
          className="absolute z-[100] top-full left-0 right-0 mt-2 bg-white rounded-xl border border-gray-200 shadow-xl overflow-hidden"
          style={{ boxShadow: '0 10px 40px rgba(0,0,0,0.15)' }}
        >
          <div className="py-2">
            {results.map((result) => (
              <Link
                key={result.slug}
                to={`/help/${result.category}/${result.slug}`}
                onClick={() => setShowDropdown(false)}
                className="block px-4 py-3 hover:bg-gray-50 transition-colors"
              >
                <div className="font-medium text-gray-900">{stripMarkdown(result.title)}</div>
                <div className="text-sm text-gray-500 mt-1 line-clamp-1">
                  {stripMarkdown(result.excerpt)}
                </div>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-xs text-violet-600 bg-violet-100 px-2 py-0.5 rounded-full">
                    {result.category_name}
                  </span>
                  <span className="text-xs text-gray-400">
                    {result.similarity}% match
                  </span>
                </div>
              </Link>
            ))}
          </div>
          <div className="border-t border-gray-100 px-4 py-2 bg-gray-50">
            <button
              onClick={handleSubmit}
              className="text-sm text-violet-600 hover:underline"
            >
              See all results for "{query}"
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
